/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/formDefaults.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    Default values and form state factories for Gateway forms.
────────────────────────────────────────────────────────────────*/

import { GatewayFormState, FieldValue } from '../types';
import { FIELD_CONFIG } from './fieldConfig';

/**
 * Compute default settings from field configuration
 */
export function computeDefaultSettings(): Record<string, FieldValue> {
  const defaults: Record<string, FieldValue> = {};
  Object.values(FIELD_CONFIG).forEach((fields) => {
    fields.forEach((field) => {
      if (field.default !== undefined) {
        defaults[field.name] = field.default;
      }
    });
  });
  return defaults;
}

/**
 * Default settings constant
 */
export const DEFAULT_SETTINGS = computeDefaultSettings();

/**
 * Create empty form state
 */
export function createEmptyFormState(): GatewayFormState {
  return {
    name: '',
    description: '',
    enabled: true,
    gatewaySipProfileId: null,
    dialProfileId: null,
    settings: { ...DEFAULT_SETTINGS }
  };
}

/**
 * Clone form state
 */
export function cloneFormState(state: GatewayFormState): GatewayFormState {
  return {
    name: state.name,
    description: state.description,
    enabled: state.enabled,
    gatewaySipProfileId: state.gatewaySipProfileId,
    dialProfileId: state.dialProfileId,
    settings: { ...state.settings }
  };
}

/**
 * Serialize form state for change detection
 */
export function serialiseFormState(state: GatewayFormState): string {
  const sortedSettings = Object.keys(state.settings)
    .sort((a, b) => a.localeCompare(b))
    .reduce<Record<string, FieldValue>>((acc, key) => {
      acc[key] = state.settings[key];
      return acc;
    }, {});

  return JSON.stringify({
    name: state.name?.trim() ?? '',
    description: state.description?.trim() ?? '',
    enabled: state.enabled,
    gatewaySipProfileId: state.gatewaySipProfileId ?? null,
    dialProfileId: state.dialProfileId ?? null,
    settings: sortedSettings
  });
}

